import clsx from 'clsx';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

const controlClass =
  'w-full rounded-none border border-ink-600 bg-ink-850 px-3 py-2 text-sm text-bone placeholder:text-ink-500 ' +
  'focus:border-accent focus:outline-none disabled:opacity-60 aria-[invalid=true]:border-danger';

interface FieldWrapperProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | string[];
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}

export function FieldWrapper({ label, hint, error, children, className }: FieldWrapperProps) {
  const id = useId();
  const errors = Array.isArray(error) ? error : error ? [error] : [];
  const describedBy = errors.length || hint ? `${id}-desc` : undefined;
  return (
    <div className={clsx('space-y-1.5', className)}>
      {label && (
        <label
          htmlFor={id}
          className="block text-xs font-semibold tracking-wider text-ink-300 uppercase"
        >
          {label}
        </label>
      )}
      {children(id, describedBy)}
      {(errors.length > 0 || hint) && (
        <p
          id={describedBy}
          className={clsx('text-xs', errors.length ? 'text-danger' : 'text-ink-400')}
        >
          {errors.length ? errors.join(' · ') : hint}
        </p>
      )}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | string[];
  wrapperClassName?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, wrapperClassName, className, ...rest },
  ref,
) {
  return (
    <FieldWrapper label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <input
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={clsx(controlClass, className)}
          {...rest}
        />
      )}
    </FieldWrapper>
  );
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | string[];
  wrapperClassName?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, wrapperClassName, className, children, ...rest },
  ref,
) {
  return (
    <FieldWrapper label={label} hint={hint} error={error} className={wrapperClassName}>
      {(id, describedBy) => (
        <select
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={clsx(controlClass, 'select-chevron appearance-none pr-8', className)}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldWrapper>
  );
});
